'use client';
import React, { useEffect, useState } from 'react';
import { cn } from '@/lib/utils';
import { ChevronDown, ChevronRight, BookOpen } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { ScrollArea } from '@/components/ui/scroll-area';

interface TocItem {
  id: string;
  text: string;
  level: number;
  progress?: number;
  children?: TocItem[];
}

interface EnhancedTableOfContentsProps {
  content: string;
  className?: string;
}

export function EnhancedTableOfContents({ content, className }: EnhancedTableOfContentsProps) {
  const [tocItems, setTocItems] = useState<TocItem[]>([]);
  const [activeId, setActiveId] = useState<string>('');
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [readingProgress, setReadingProgress] = useState<Record<string, number>>({});
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());

  useEffect(() => {
    // Wait for content to render in the DOM, then read actual heading IDs
    const timer = requestAnimationFrame(() => {
      const articleContent = document.querySelector('#article-content');
      if (!articleContent) return;

      const headings = articleContent.querySelectorAll('h1, h2, h3, h4, h5, h6');
      const items: TocItem[] = [];

      headings.forEach((heading, index) => {
        const level = parseInt(heading.tagName.charAt(1));
        const text = heading.textContent || '';

        // Use existing DOM ID if present; otherwise generate and assign one
        if (!heading.id) {
          heading.id = text.toLowerCase().replace(/\s+/g, '-').replace(/[^\w-]/g, '') || `heading-${index}`;
        }

        items.push({ id: heading.id, text, level, progress: 0 });
      });

      setTocItems(items);
    });

    return () => cancelAnimationFrame(timer);
  }, [content]);

  useEffect(() => {
    const handleScroll = () => {
      const headings = tocItems.map(item => document.getElementById(item.id)).filter(Boolean);
      const progress: Record<string, number> = {};
      
      for (let i = headings.length - 1; i >= 0; i--) {
        const heading = headings[i];
        if (heading) {
          const rect = heading.getBoundingClientRect();
          const nextHeading = headings[i + 1];
          
          if (rect.top <= 100) {
            setActiveId(heading.id);
            
            // Calculate reading progress for this section
            if (nextHeading) {
              const nextRect = nextHeading.getBoundingClientRect();
              const sectionHeight = nextHeading.offsetTop - heading.offsetTop;
              const scrolledInSection = Math.max(0, heading.offsetTop - window.scrollY + 100);
              progress[heading.id] = Math.min(100, Math.max(0, (scrolledInSection / sectionHeight) * 100));
            } else {
              const docHeight = document.documentElement.scrollHeight;
              const remaining = docHeight - heading.offsetTop;
              const scrolled = Math.max(0, window.scrollY - heading.offsetTop + 100);
              progress[heading.id] = Math.min(100, (scrolled / remaining) * 100);
            }
            break;
          }
        }
      }
      
      setReadingProgress(progress);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [tocItems]);

  const handleClick = (id: string) => {
    setActiveId(id);
    const element = document.getElementById(id);
    if (element) {
      const offset = 120; // Slightly more than detection threshold (100) to ensure correct highlight
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;
      
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };


  const groupTocItems = (items: TocItem[]): TocItem[] => {
    const grouped: TocItem[] = [];
    let currentH2: TocItem | null = null;
    
    items.forEach((item) => {
      if (item.level === 1 || item.level === 2) {
        currentH2 = { ...item, children: [] };
        grouped.push(currentH2);
      } else if (currentH2 && item.level >= 3) {
        currentH2.children!.push(item);
      } else {
        // No parent h2 — treat as top-level
        grouped.push({ ...item, children: [] });
      }
    });
    
    return grouped;
  };

  const toggleGroup = (id: string) => {
    const newSet = new Set(collapsedGroups);
    if (newSet.has(id)) {
      newSet.delete(id);
    } else {
      newSet.add(id);
    }
    setCollapsedGroups(newSet);
  };

  if (tocItems.length === 0) return null;

  const groupedItems = groupTocItems(tocItems);

  return (
    <div className={cn("hidden lg:block bg-gradient-to-br from-muted/30 to-muted/50 rounded-xl p-4 backdrop-blur-sm border", className)}>
      <Collapsible open={!isCollapsed} onOpenChange={(open) => setIsCollapsed(!open)}>
        <CollapsibleTrigger asChild>
          <Button variant="ghost" className="w-full justify-between h-auto p-2 hover:bg-muted/50">
            <div className="flex items-center gap-2">
              <BookOpen className="h-4 w-4 text-primary" />
              <h3 className="font-semibold text-sm uppercase tracking-wide">
                Table of Contents
              </h3>
            </div>
            {isCollapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </Button>
        </CollapsibleTrigger>
        
        <CollapsibleContent className="pt-3">
          <div className="max-h-[calc(100vh-200px)] overflow-y-auto overflow-x-hidden">
            <div className="space-y-1 pr-4">
              {groupedItems.map((item) => {
                const hasChildren = item.children && item.children.length > 0;
                
                if (hasChildren) {
                  return (
                    <Collapsible
                      key={item.id}
                      open={!collapsedGroups.has(item.id)}
                      onOpenChange={() => toggleGroup(item.id)}
                    >
                      <div className="flex items-center gap-0.5">
                        <button
                          onClick={() => handleClick(item.id)}
                          className={cn(
                            "text-left text-sm transition-all duration-200 hover:text-primary flex-1 min-w-0 overflow-hidden rounded-md px-2 py-1.5 hover:bg-muted/50",
                            item.level === 1 && "font-semibold text-base",
                            item.level === 2 && "font-medium",
                            activeId === item.id && "text-primary bg-primary/10 font-medium border-l-2 border-primary"
                          )}
                        >
                          <span className="block truncate">{item.text}</span>
                        </button>
                        <CollapsibleTrigger asChild>
                          <Button
                            variant="ghost"
                            size="sm"
                            aria-label={collapsedGroups.has(item.id) ? 'Expand section' : 'Collapse section'}
                            className="h-7 w-7 p-0 shrink-0 text-muted-foreground hover:text-foreground"
                          >
                            {collapsedGroups.has(item.id) ? (
                              <ChevronRight className="h-3.5 w-3.5" />
                            ) : (
                              <ChevronDown className="h-3.5 w-3.5" />
                            )}
                          </Button>
                        </CollapsibleTrigger>
                      </div>
                      <CollapsibleContent>
                        <div className="space-y-1 mt-1">
                          {item.children!.map((child) => (
                            <button
                              key={child.id}
                              onClick={() => handleClick(child.id)}
                              className={cn(
                                "text-left text-xs transition-all duration-200 hover:text-primary block w-full rounded-md px-2 py-1 hover:bg-muted/50",
                                child.level === 3 && "pl-6 text-muted-foreground",
                                child.level >= 4 && "pl-8 text-muted-foreground",
                                activeId === child.id && "text-primary bg-primary/10 font-medium border-l-2 border-primary"
                              )}
                            >
                              <span className="block truncate">{child.text}</span>
                            </button>
                          ))}
                        </div>
                      </CollapsibleContent>
                    </Collapsible>
                  );
                }

                return (
                  <button
                    key={item.id}
                    onClick={() => handleClick(item.id)}
                    className={cn(
                      "text-left text-sm transition-all duration-200 hover:text-primary block w-full overflow-hidden rounded-md px-2 py-1.5 hover:bg-muted/50",
                      item.level === 1 && "font-semibold text-base",
                      item.level === 2 && "font-medium",
                      activeId === item.id && "text-primary bg-primary/10 font-medium border-l-2 border-primary"
                    )}
                  >
                    <span className="block truncate">{item.text}</span>
                  </button>
                );
              })}
            </div>
          </div>
          
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
}